import React, { useState } from 'react';

interface Student {
  id: string;
  name: string;
  avatar: string;
}

interface RewardModalProps {
  isOpen: boolean;
  onClose: () => void;
  students: Student[];
  onConfirmReward: (selectedStudents: Student[], amount: number) => void;
  isAwarding?: boolean;
}

const RewardModal: React.FC<RewardModalProps> = ({
  isOpen,
  onClose,
  students,
  onConfirmReward,
  isAwarding = false
}) => {
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [rewardAmount, setRewardAmount] = useState<number>(50);

  if (!isOpen) return null;

  const handleStudentClick = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleRewardConfirm = () => {
    const selectedStudentObjects = students.filter(student => selectedStudents.has(student.id));
    onConfirmReward(selectedStudentObjects, rewardAmount);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedStudents(new Set());
    setRewardAmount(50);
    onClose();
  };

  const predefinedAmounts = [25, 50, 100, 150, 200];

  return (
    <div className="attendance-modal-overlay" onClick={handleOverlayClick}>
      <div className="attendance-modal" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="attendance-modal-header">
          <h2>Reward Students 💰</h2>
          <button className="close-modal-btn" onClick={handleClose}>×</button>
        </div>

        <div className="attendance-modal-content" style={{ overflowY: 'auto', flexGrow: 1 }}>
          <div className="reward-amount-section" style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>Select Musecash Amount</h3>
            <div className="amount-buttons" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {predefinedAmounts.map((amount) => (
                <button
                  key={amount}
                  className={`amount-button ${rewardAmount === amount ? 'selected' : ''}`}
                  onClick={() => setRewardAmount(amount)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: `2px solid ${rewardAmount === amount ? '#4CAF50' : '#ddd'}`,
                    backgroundColor: rewardAmount === amount ? '#4CAF50' : 'white',
                    color: rewardAmount === amount ? 'white' : '#333',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {amount} 💰
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label htmlFor="custom-amount" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                Custom Amount:
              </label>
              <input
                id="custom-amount"
                type="number"
                value={rewardAmount}
                onChange={(e) => setRewardAmount(Math.max(0, parseInt(e.target.value) || 0))}
                min="0"
                max="1000"
                style={{
                  padding: '0.5rem',
                  border: '2px solid #ddd',
                  borderRadius: '4px',
                  width: '80px',
                  fontSize: '0.9rem'
                }}
              />
              <span style={{ fontSize: '0.9rem', color: '#666' }}>Musecash</span>
            </div>
          </div>

          <p className="attendance-instructions">
            Select students to reward with {rewardAmount} Musecash each
          </p>

          <div className="attendance-student-grid">
            {students.map((student) => (
              <div
                key={student.id}
                className={`attendance-student-item ${
                  selectedStudents.has(student.id) ? 'selected' : ''
                }`}
                onClick={() => handleStudentClick(student.id)}
              >
                <div className="attendance-student-avatar-container">
                  <img
                    src={student.avatar}
                    alt={student.name}
                    className="attendance-student-avatar"
                  />
                  {selectedStudents.has(student.id) && (
                    <div className="attendance-checkmark">✓</div>
                  )}
                </div>
                <span className="attendance-student-name">{student.name}</span>
              </div>
            ))}
          </div>

        </div>
        <div className="attendance-modal-footer" style={{ flexShrink: 0, padding: '1rem 2rem' }}>
            <div className="selected-count">
              {selectedStudents.size} of {students.length} selected
            </div>
            <button
              className="all-selected-btn"
              onClick={handleRewardConfirm}
              disabled={selectedStudents.size === 0 || isAwarding}
              style={{
                backgroundColor: selectedStudents.size === 0 || isAwarding ? '#ccc' : '#4CAF50',
                cursor: selectedStudents.size === 0 || isAwarding ? 'not-allowed' : 'pointer'
              }}
            >
              {isAwarding ? (
                <>
                  <span style={{ marginRight: '0.5rem' }}>🔄</span>
                  Awarding...
                </>
              ) : (
                <>
                  <span style={{ marginRight: '0.5rem' }}>💰</span>
                  Reward {selectedStudents.size} Student{selectedStudents.size !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
      </div>
    </div>
  );
};

export default RewardModal;